import type { estypes } from '@elastic/elasticsearch';

import {
  BET_SIDE_CAP,
  BETS_PER_SIDE_CAP,
  MARKET_BUCKET_CAP,
} from '~/search-domain/pipelines/match-centre/query-factory';
import {
  type AggregatedMatchupCentreResultBody,
  type MatchupCentreAggregations,
  type MatchupCentreESResult,
  type MatchupCentreESResultBody,
  type MatchupCentreMarket,
  type MatchupCentreResult,
} from '~/search-domain/pipelines/match-centre/types';
import { type Loggable } from '~/search-domain/types';
import { type BettingInsightsElasticSearchContext } from '~/search-domain/types/contracts';
import { replaceResponseHits } from '~/search-domain/utils';
import {
  extractMatchupCentreBet,
  extractMatchupCentreMarket,
  extractMatchupCentreSubevent,
  isMarketSupported,
  type MatchupCentreBet,
} from '~/search-domain/utils/response-factories';

export type UniqueBetsAggregation = {
  value: number;
};

export type OriginalAggregations = {
  stable_total?: { value?: number };
  subevents: SubeventsAggregation;
};

export type SubeventsAggregation = {
  buckets: SubeventsBucket[];
  doc_count_error_upper_bound?: number;
};

export type AggregationBucket = {
  key: number;
};

export type SubeventsBucket = AggregationBucket & {
  markets: { buckets: MarketsBucket[]; sum_other_doc_count?: number };
};

export type MarketsBucket = AggregationBucket & {
  bets: { buckets: BetSideBucket[]; sum_other_doc_count?: number };
};

export type BetsBucket = AggregationBucket & {
  best_odds: {
    hits: {
      hits: estypes.SearchHit<MatchupCentreESResult>[];
    };
  };
};

/**
 * betId buckets sit one level down under a `line.name` terms agg so that each side of a market is
 * bounded separately — see the bets aggregation in `query-factory.ts`.
 */
export type BetSideBucket = {
  by_bet: { buckets: BetsBucket[]; sum_other_doc_count?: number };
  key: string;
};

/**
 * Flattens the side buckets back to a plain betId list, side-major rather than globally `_count
 * desc`. The resulting order IS load-bearing: it flows unsorted into `market.bets` below, and
 * `MatchupCard.tsx` picks the rendered bet per side via `.find()` on that exact array — with the
 * per-side bucket cap, multiple bets can share `teamType: 'home'`, so `.find()` returning the right
 * one depends on each side's `by_bet` bucket staying `_count desc` from Elasticsearch.
 *
 * `sortBetsBySequence` does NOT cover this path — it only reorders bets for
 * `createBettingSplitsUIConfig.tsx` (betting-splits), a separate consumer of the same
 * `MatchupCentreResult` type. Do not assume this order is free to change.
 */
function toBetBuckets(buckets: BetSideBucket[]): BetsBucket[] {
  return buckets.flatMap(bucket => bucket.by_bet.buckets);
}
export type { MatchupCentreESResultBody, AggregatedMatchupCentreResultBody };

type SubeventExtractor = (
  result: MatchupCentreESResult,
  userPreference: BettingInsightsElasticSearchContext['userPreference'],
) => MatchupCentreResult;

type AggregationState = {
  originalHits: Map<number, estypes.SearchHit<MatchupCentreESResult>>;
  subevents: Map<number, MatchupCentreResult>;
};

export async function createResponseFactory(
  this: Loggable,
  config: BettingInsightsElasticSearchContext,
  searchResponse: MatchupCentreESResultBody,
  from = 0,
  hitsPerPage = 10,
): Promise<AggregatedMatchupCentreResultBody> {
  return buildMatchupCentreResponse.call(this, config, searchResponse, from, hitsPerPage, extractMatchupCentreSubevent);
}

async function buildMatchupCentreResponse(
  this: Loggable,
  config: BettingInsightsElasticSearchContext,
  searchResponse: MatchupCentreESResultBody,
  from: number,
  hitsPerPage: number,
  extractSubevent: SubeventExtractor,
): Promise<AggregatedMatchupCentreResultBody> {
  const aggregations = searchResponse.aggregations as OriginalAggregations;
  const { stable_total: stableTotal, subevents: subeventsGroup, ...baseAggregations } = aggregations;
  // A non-zero value here means the terms agg's shard-level candidate selection (see shard_size in
  // query-factory.ts) may have pruned a subevent before its ordering metric was even evaluated --
  // ES reports this as doc count error, but the risk it signals is a missing or misordered card, not
  // a wrong count.
  if (subeventsGroup.doc_count_error_upper_bound) {
    this.logger.warn(
      `[Matchup Centre] subevents terms agg reported doc_count_error_upper_bound of ${subeventsGroup.doc_count_error_upper_bound}; a subevent may be missing or misordered`,
    );
  }

  const { originalHits, subevents } = aggregateSubevents.call(this, subeventsGroup, from, hitsPerPage, {
    config,
    extractSubevent,
  });

  const hits = buildHits(subevents, originalHits, hitsPerPage);

  return replaceResponseHits(searchResponse, {
    aggregations: baseAggregations as MatchupCentreAggregations,
    hits,
    isWalled: config.isWalled,
    totalRelation: 'eq',
    totalValue: getTotalSubevents({
      bucketCount: subeventsGroup.buckets.length,
      cardinality: stableTotal?.value,
      from,
      hitsPerPage,
      isWalled: config.isWalled,
    }),
  });
}

// The subevents terms agg is page-scoped, so the bucket list is only the full population when it is
// shorter than the page window; beyond that the cardinality agg carries the total.
function getTotalSubevents(params: {
  bucketCount: number;
  cardinality?: number;
  from: number;
  hitsPerPage: number;
  isWalled?: boolean;
}) {
  // A walled request collapses the subevents bucket size to the over-fetch minimum
  // (`getSubeventBucketSize`), so its bucket list is deliberately far shorter than the population
  // and the length test below would read it as the whole set. Without this a walled GZ card counted
  // 3 matches where there were 16, and paginated to one page.
  if (params.isWalled) {
    return params.cardinality ?? params.bucketCount;
  }

  const hasMore = params.bucketCount > params.from + params.hitsPerPage;
  return hasMore ? (params.cardinality ?? params.bucketCount) : params.bucketCount;
}

function aggregateSubevents(
  this: Loggable,
  subeventsGroup: SubeventsAggregation,
  from: number,
  hitsPerPage: number,
  params: {
    config: BettingInsightsElasticSearchContext;
    extractSubevent: SubeventExtractor;
  },
): AggregationState {
  const state: AggregationState = {
    subevents: new Map<number, MatchupCentreResult>(),
    originalHits: new Map<number, estypes.SearchHit<MatchupCentreESResult>>(),
  };

  // Process more subevents than requested because some are filtered out by market quality rules.
  const processCount = Math.min(subeventsGroup.buckets.length, from + hitsPerPage * 3);
  const relevantSubevents = subeventsGroup.buckets.slice(from, processCount);

  for (const { key: subeventId, markets } of relevantSubevents) {
    let subevent = state.subevents.get(subeventId);

    // `sum_other_doc_count` counts documents, not buckets/bets — see tests/response-factory.test.ts.
    if (markets.sum_other_doc_count) {
      this.logger.warn(
        `[Matchup Centre] MARKET_BUCKET_CAP (${MARKET_BUCKET_CAP}) truncated markets for subevent ${subeventId}: ${markets.sum_other_doc_count} document(s) in dropped market buckets`,
      );
    }

    for (const { bets, key: marketId } of markets.buckets) {
      if (bets.sum_other_doc_count) {
        this.logger.warn(
          `[Matchup Centre] BET_SIDE_CAP (${BET_SIDE_CAP}) truncated bet sides for subevent ${subeventId}, market ${marketId}: ${bets.sum_other_doc_count} document(s) in dropped bet-side buckets`,
        );
      }

      for (const side of bets.buckets) {
        if (side.by_bet.sum_other_doc_count) {
          this.logger.warn(
            `[Matchup Centre] BETS_PER_SIDE_CAP (${BETS_PER_SIDE_CAP}) truncated bets for subevent ${subeventId}, market ${marketId}, side ${side.key}: ${side.by_bet.sum_other_doc_count} document(s) in dropped bet buckets`,
          );
        }
      }

      let market = subevent?.markets.find((item: MatchupCentreMarket) => item.marketId === marketId);

      for (const { best_odds } of toBetBuckets(bets.buckets)) {
        for (const hit of best_odds.hits.hits) {
          const result = hit._source;
          if (!result) continue;

          subevent = subevent ?? createSubevent.call(this, result, hit, state, params.config, params.extractSubevent);
          if (!subevent) continue;

          market = market ?? createMarket.call(this, result, marketId, subevent, params.config);
          if (!market) continue;

          const bet = createBet.call(this, result);
          if (!bet) continue;
          upsertMarketBet(market, bet);
        }
      }

      if (subevent && market && shouldIncludeMarket(market)) {
        subevent.markets.push(market);
      }
    }
  }

  return state;
}

function createSubevent(
  this: Loggable,
  result: MatchupCentreESResult,
  hit: estypes.SearchHit<MatchupCentreESResult>,
  state: AggregationState,
  config: BettingInsightsElasticSearchContext,
  extractSubevent: SubeventExtractor,
): MatchupCentreResult | undefined {
  try {
    const subevent = extractSubevent(result, config.userPreference);
    subevent.markets = [];
    state.subevents.set(result.subeventId, subevent);
    state.originalHits.set(result.subeventId, hit);
    return subevent;
  } catch (error) {
    this.logger.warn(`Failed to parse subevent fields: ${result.subeventId}`, error);
    return undefined;
  }
}

function createMarket(
  this: Loggable,
  result: MatchupCentreESResult,
  marketId: number,
  subevent: MatchupCentreResult,
  config: BettingInsightsElasticSearchContext,
): MatchupCentreMarket | undefined {
  try {
    const market = extractMatchupCentreMarket(result);
    if (!isMarketSupported(market, config)) return undefined;
    subevent.betTypeId.push(market.betTypeId);
    market.bets = [];
    return market;
  } catch (error) {
    this.logger.warn(`Failed to parse market fields: ${marketId}`, error);
    return undefined;
  }
}

function createBet(this: Loggable, result: MatchupCentreESResult): MatchupCentreBet | undefined {
  try {
    return extractMatchupCentreBet(result);
  } catch (error) {
    this.logger.warn(
      `[Matchup Centre] Failed to parse bet - SubeventId: ${result.subeventId}, MarketId: ${result.marketId}, BetId: ${result.betId}`,
      {
        error: error instanceof Error ? error.message : String(error),
        betName: result.betName,
        bookmakerCode: result.bookmakerCode,
      },
    );
    return undefined;
  }
}

function upsertMarketBet(market: MatchupCentreMarket, bet: MatchupCentreBet): void {
  const marketBet = toMarketBet(bet);
  const existingBetIndex = market.bets.findIndex(existingBet => existingBet.betId === bet.betId);
  if (existingBetIndex === -1) {
    market.bets.push(marketBet);
    return;
  }
  market.bets[existingBetIndex] = marketBet;
}

function toMarketBet(bet: MatchupCentreBet): MatchupCentreMarket['bets'][number] {
  // Normalization boundary: parser returns some matchup fields as optional/null,
  // while market model keeps a strict bet shape for downstream UI consumption.
  return {
    ...bet,
    betCount: bet.betCount ?? 0,
    betPercentage: bet.betPercentage ?? 0,
    expectedValue: bet.expectedValue ?? 0,
    expectedValueStatus: bet.expectedValueStatus ?? '',
    stakeBetDiff: bet.stakeBetDiff ?? 0,
    stakePercentage: bet.stakePercentage ?? 0,
  };
}

function shouldIncludeMarket(market: MatchupCentreMarket): boolean {
  // Inclusion rules:
  // - 3-way (with Draw): require exactly 2 non-Draw bets
  // - 2-way markets: allow partial data (>=1 bet) to keep cards renderable
  // - Over/Under: allow partial data (>=1 bet)
  const nonDrawBets = market.bets.filter(bet => bet.betName !== 'Draw');
  const hasDrawOption = market.bets.some(bet => bet.betName === 'Draw');
  const isOverUnderMarket = market.marketClassification === 'OVER_UNDER';

  return (
    (hasDrawOption && nonDrawBets.length === 2) ||
    (!hasDrawOption && !isOverUnderMarket && market.bets.length >= 1) ||
    (!hasDrawOption && isOverUnderMarket && market.bets.length >= 1)
  );
}

function buildHits(
  subevents: Map<number, MatchupCentreResult>,
  originalHits: Map<number, estypes.SearchHit<MatchupCentreESResult>>,
  hitsPerPage: number,
) {
  return (
    Array.from(subevents.values())
      .filter(({ markets }) => markets.length > 0)
      // Limit to requested page size after filtering unsupported/invalid markets.
      .slice(0, hitsPerPage)
      .map(subevent => {
        const originalHit = originalHits.get(subevent.subeventId);
        return {
          ...(originalHit || {}),
          _index: originalHit?._index || '',
          _source: subevent,
        };
      })
  );
}
